import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/Bits";
import { DemoBadge } from "@/components/DemoBadge";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";

export default function AppPaperTrading() {
  const { money } = useCurrency();
  const [acct, setAcct] = useState(null);
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState({ symbol: "BTC/USD", side: "BUY", quantity: 0.1, order_type: "market", limit_price: "", stop_loss: "", take_profit: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/paper/account").then((r) => setAcct(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/markets").then((r) => setAssets(r.data.assets)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = { symbol: form.symbol, side: form.side, quantity: +form.quantity, order_type: form.order_type };
      if (form.order_type === "limit" && form.limit_price) body.limit_price = +form.limit_price;
      if (form.stop_loss) body.stop_loss = +form.stop_loss;
      if (form.take_profit) body.take_profit = +form.take_profit;
      const { data } = await api.post("/paper/order", body);
      toast.success(`Order filled @ ${fmt(data.fill_price)}`);
      load();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Order failed"); }
    finally { setBusy(false); }
  };

  const close = async (id) => { await api.post(`/paper/close/${id}`); toast.success("Position closed"); load(); };
  const reset = async () => { await api.post("/paper/reset"); toast.success("Account reset to $100,000"); load(); };

  return (
    <DashboardLayout title="Paper Trading">
      <div className="mb-4 flex items-center gap-2">
        <span className="rounded-md bg-warning/15 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-warning">Paper Trading — Simulated Money</span>
        <DemoBadge />
      </div>

      {acct && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
          <StatCard label="Balance" value={money(acct.balance)} testid="paper-balance" />
          <StatCard label="Equity" value={money(acct.equity)} testid="paper-equity" />
          <StatCard label="Unrealized" value={money(acct.unrealized_pnl)} accent={acct.unrealized_pnl >= 0 ? "text-bullish" : "text-bearish"} />
          <StatCard label="Total P&L" value={money(acct.total_pnl)} accent={acct.total_pnl >= 0 ? "text-bullish" : "text-bearish"} testid="paper-total-pnl" />
          <StatCard label="Win Rate" value={`${acct.win_rate}%`} />
          <StatCard label="Profit Factor" value={acct.profit_factor} />
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* Order form */}
        <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-5" data-testid="order-form">
          <h3 className="font-display text-lg font-semibold">New Order</h3>
          <div className="mt-4 space-y-3 text-sm">
            <div>
              <label className="mb-1 block text-[10px] uppercase text-muted-foreground">Asset</label>
              <select value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} data-testid="order-symbol" className="w-full rounded-lg border border-border bg-card px-3 py-2">
                {assets.map((a) => <option key={a.symbol} value={a.symbol}>{`${a.symbol} · ${fmt(a.price)}`}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setForm({ ...form, side: "BUY" })} data-testid="order-buy" className={`rounded-lg py-2 font-semibold ${form.side === "BUY" ? "bg-bullish text-white" : "border border-border"}`}>BUY</button>
              <button type="button" onClick={() => setForm({ ...form, side: "SELL" })} data-testid="order-sell" className={`rounded-lg py-2 font-semibold ${form.side === "SELL" ? "bg-bearish text-white" : "border border-border"}`}>SELL</button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {["market", "limit"].map((t) => <button key={t} type="button" onClick={() => setForm({ ...form, order_type: t })} className={`rounded-lg py-2 text-xs capitalize ${form.order_type === t ? "bg-primary text-primary-foreground" : "border border-border"}`}>{t}</button>)}
            </div>
            <Inp label="Quantity" value={form.quantity} onChange={(v) => setForm({ ...form, quantity: v })} testid="order-qty" />
            {form.order_type === "limit" && <Inp label="Limit price" value={form.limit_price} onChange={(v) => setForm({ ...form, limit_price: v })} />}
            <Inp label="Stop loss (opt)" value={form.stop_loss} onChange={(v) => setForm({ ...form, stop_loss: v })} />
            <Inp label="Take profit (opt)" value={form.take_profit} onChange={(v) => setForm({ ...form, take_profit: v })} />
            <button disabled={busy} data-testid="order-submit" className="w-full rounded-xl bg-primary py-2.5 font-semibold text-primary-foreground disabled:opacity-60">Place Order</button>
          </div>
        </form>

        {/* Positions + history */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="mb-3 flex items-center justify-between"><h3 className="font-display text-lg font-semibold">Open Positions</h3><button onClick={reset} data-testid="paper-reset" className="text-xs text-muted-foreground hover:text-foreground">Reset account</button></div>
            {acct?.open_positions?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase text-muted-foreground"><th className="py-2">Asset</th><th>Side</th><th className="text-right">Qty</th><th className="text-right">Entry</th><th className="text-right">Now</th><th className="text-right">P&L</th><th></th></tr></thead>
                  <tbody>
                    {acct.open_positions.map((p) => (
                      <tr key={p.id} className="border-t border-border" data-testid={`position-${p.id}`}>
                        <td className="py-2 font-semibold">{p.symbol}</td>
                        <td className={p.side === "BUY" ? "text-bullish" : "text-bearish"}>{p.side}</td>
                        <td className="text-right font-mono">{fmt(p.quantity, 4)}</td>
                        <td className="text-right font-mono">{fmt(p.entry_price)}</td>
                        <td className="text-right font-mono">{fmt(p.current_price)}</td>
                        <td className={`text-right font-mono ${p.pnl >= 0 ? "text-bullish" : "text-bearish"}`}>{fmt(p.pnl)}</td>
                        <td className="text-right"><button onClick={() => close(p.id)} data-testid={`close-${p.id}`} className="rounded-lg border border-border px-2 py-1 text-xs hover:bg-secondary">Close</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-sm text-muted-foreground">No open positions. Place an order to begin.</p>}
          </div>

          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="mb-3 font-display text-lg font-semibold">Trade History</h3>
            {acct?.trade_history?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase text-muted-foreground"><th className="py-2">Asset</th><th>Side</th><th className="text-right">Entry</th><th className="text-right">Exit</th><th className="text-right">P&L</th></tr></thead>
                  <tbody>
                    {acct.trade_history.map((t) => (
                      <tr key={t.id} className="border-t border-border">
                        <td className="py-2 font-semibold">{t.symbol}</td>
                        <td className={t.side === "BUY" ? "text-bullish" : "text-bearish"}>{t.side}</td>
                        <td className="text-right font-mono">{fmt(t.entry_price)}</td>
                        <td className="text-right font-mono">{fmt(t.exit_price)}</td>
                        <td className={`text-right font-mono ${t.pnl >= 0 ? "text-bullish" : "text-bearish"}`}>{fmt(t.pnl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-sm text-muted-foreground">No closed trades yet.</p>}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Inp({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] uppercase text-muted-foreground">{label}</label>
      <input type="number" step="any" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full rounded-lg border border-border bg-card px-3 py-2 font-mono" />
    </div>
  );
}
